import { interleaveTwoArrays } from '@/src/homepage/components/utils/arrayUtils';

type Identifiable = { id: string };

/**
 * Merges paid placements into an organic feed at fixed slots, keeping one card per event.
 *
 * The auction and search are answered independently, so the same event can come back from both. Rendering it twice
 * burns a slot on an event the feed already has and gives two cards the same id, which downstream ranking then cannot
 * tell apart. The paid copy wins — it is the one being billed — so the organic duplicate is dropped before interleaving.
 *
 * Callers must pass the events they will actually render: `positions` are slots in the final visible list, so any
 * filtering (unactionable events, layout caps) has to happen before this, not after.
 */
export function mergePromotedEvents<Organic extends Identifiable, Promoted extends Identifiable>(
    organicEvents: Organic[],
    promotedEvents: Promoted[],
    positions: number[],
): (Organic | Promoted)[] {
    if (!promotedEvents.length) {
        return [...organicEvents];
    }

    const promotedIds = new Set(promotedEvents.map((event) => event.id));
    const withoutDuplicates = organicEvents.filter((event) => !promotedIds.has(event.id));

    return interleaveTwoArrays<Organic | Promoted>(withoutDuplicates, promotedEvents, positions);
}
